import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { Plus, User, Users } from "lucide-react";

type Patient = {
  id: string;
  full_name: string;
  avatar_url: string | null;
};

export function RecentPatients() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecentPatients();
  }, []);

  const loadRecentPatients = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Usar edge function para buscar pacientes por última atividade
      const { data, error } = await supabase.functions.invoke('get-recent-patients-with-activity');

      if (error) {
        console.error("Error loading recent patients:", error);
        // Fallback para query simples se edge function falhar
        const { data: fallbackData, error: fallbackError } = await supabase
          .from("patients_onboarding")
          .select("id, full_name, avatar_url")
          .eq("owner_id", user.id)
          .order("updated_at", { ascending: false })
          .limit(5);
        
        if (!fallbackError && fallbackData) {
          setPatients(fallbackData);
        }
      } else {
        setPatients(data || []);
      }
    } catch (error) {
      console.error("Error loading recent patients:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-border/50 shadow-sm h-full min-h-[260px] flex flex-col">
        <CardHeader className="pb-3">
          <div className="flex items-start gap-2">
            <Users className="h-4 w-4 text-primary mt-0.5" />
            <div className="space-y-1">
              <CardTitle className="text-sm">Pacientes Recentes</CardTitle>
              <CardDescription className="text-xs">
                Continue os últimos atendimentos acessados
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 flex-1">
          <div className="flex flex-wrap items-start gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="inline-flex w-[86px] flex-col items-center gap-2 animate-pulse">
                <div className="w-12 h-12 rounded-xl bg-muted" />
                <div className="h-3 w-20 bg-muted rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50 shadow-sm h-full min-h-[260px] flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-2">
          <Users className="h-4 w-4 text-primary mt-0.5" />
          <div className="space-y-1">
            <CardTitle className="text-sm">Pacientes Recentes</CardTitle>
            <CardDescription className="text-xs">
              Continue os últimos atendimentos acessados
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 flex-1">
        <div className="flex flex-wrap items-start gap-4">
          <button
            type="button"
            onClick={() => navigate("/app/pacientes?create=manual")}
            className="group inline-flex w-[86px] flex-col items-center gap-2"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/30 bg-primary/5 text-primary transition-colors group-hover:bg-primary/10">
              <Plus className="h-6 w-6" aria-hidden />
            </span>
            <span className="text-[13px] font-semibold leading-tight text-center text-foreground">
              Adicionar
            </span>
          </button>

          {patients.map((patient) => (
            <button
              type="button"
              key={patient.id}
              onClick={() => navigate(`/app/pacientes/${patient.id}`)}
              className="group inline-flex w-[86px] flex-col items-center gap-2"
            >
              <Avatar className="h-12 w-12 rounded-xl border border-primary/20">
                <AvatarImage src={patient.avatar_url || undefined} />
                <AvatarFallback className="bg-primary/10 text-primary rounded-xl">
                  <User className="h-6 w-6" />
                </AvatarFallback>
              </Avatar>
              <span className="text-[13px] font-semibold leading-tight text-center text-foreground line-clamp-2">
                {patient.full_name}
              </span>
            </button>
          ))}
        </div>

        {patients.length === 0 && (
          <p className="text-sm text-muted-foreground text-center pt-4">
            Nenhum paciente cadastrado ainda
          </p>
        )}
      </CardContent>
    </Card>
  );
}
